import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  Stack,
  Typography
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { Control, Controller, FieldValues, FieldErrors, UseFormSetValue } from "react-hook-form";
import { CONFIRMATION, MUAC_OPTIONS, OUTCOME, PANELS, RESULT_OPTIONS } from "../../constants";
import FormTextField from "../../../../components/FormTextField/FormTextField";
import InputComponents from "./InputComponents";
import Diagnosis from "../../../Assessment/Diagnosis/Diagnosis";
import ReferrerCreateSelect from "../../../Referrers/ReferrerCreateSelect";
import { isISODateString } from "../../../../helpers/validators";
import ToggleInputDate from "../../../../components/ToggleADBS";
import { fields } from "../../../../models/Client";
import { PanelsType } from "./ImnciAboveTwoMonthsForm";

interface CollapseProps {
  expanded: boolean;
  onChange: () => void;
  title: string;
  contents: JSX.Element | JSX.Element[];
}

export const Collapse = ({ expanded, contents, title, onChange }: CollapseProps): JSX.Element => (
  <Accordion expanded={expanded} onChange={onChange} variant="outlined">
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Typography fontWeight={600} fontSize="1rem">
        {title}
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0 }}>{contents}</AccordionDetails>
  </Accordion>
);

interface Props {
  control: Control<FieldValues>;
  errors: FieldErrors;
  showGeneralDangerSign: boolean;
  showAcuteRespiratoryInfection: boolean;
  showDiarrhoea: boolean;
  showEarInfection: boolean;
  showFever: boolean;
  icd: { icdCode: string; diagnosis: string } | null;
  setValue: UseFormSetValue<FieldValues>;
}

const CollapsableInputs = ({
  control,
  errors,
  setValue,
  icd,
  showAcuteRespiratoryInfection,
  showEarInfection,
  showFever,
  showDiarrhoea,
  showGeneralDangerSign
}: Props): JSX.Element => {
  const [expanded, setExpanded] = React.useState(PANELS.VITALS_AND_REFERRAL);

  const handleExpandChange = (panel: PanelsType) => {
    setExpanded(panel === expanded ? PANELS.NONE : panel);
  };

  return (
    <div>
      <Collapse
        expanded={expanded === PANELS.VITALS_AND_REFERRAL}
        onChange={() => handleExpandChange(PANELS.VITALS_AND_REFERRAL)}
        title="Vital And Referral"
        contents={
          <Grid container spacing={2} pl={2}>
            <Grid isize={{ xs: 12, sm: 6 }}>
              <FormTextField
                fullWidth
                control={control}
                label="Weight (Kg)"
                placeholder="Provide weight"
                dataTestmation="weight"
                errors={errors}
                name="weight"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormTextField
                fullWidth
                control={control}
                label="Temperature (°C)"
                placeholder="Provide temperature"
                dataTestmation="temperature"
                errors={errors}
                name="temperature"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <InputComponents
                control={control}
                name="referredBy"
                label="Referred By"
                type="radio"
                radioOptions={[
                  { label: "FCHV", value: "FCHV" },
                  { label: "PHC/ORC", value: "PHC/ORC" },
                  { label: "HF", value: "HF" }
                ]}
              />
            </Grid>
          </Grid>
        }
      />
      <Collapse
        expanded={expanded === PANELS.PATIENT_SIGN_AND_SYMPTOMS}
        onChange={() => handleExpandChange(PANELS.PATIENT_SIGN_AND_SYMPTOMS)}
        title="Patient Signs And Symptoms"
        contents={
          <Stack ml={2} gap={1}>
            {/* gds */}
            <div>
              <InputComponents
                control={control}
                name="generalDangerSign"
                label="General Danger Sign (GDS)"
                type="radio"
                radioOptions={[
                  {
                    value: CONFIRMATION.YES,
                    label: "Yes"
                  },
                  {
                    value: CONFIRMATION.NO,
                    label: "No"
                  }
                ]}
              />
              {showGeneralDangerSign && (
                <Grid
                  container
                  spacing={0}
                  sx={{
                    pl: 2,
                    borderLeft: "1px solid #ddd"
                  }}
                >
                  {[
                    {
                      name: "gdsUnableToDrink",
                      label: "Unable to drink"
                    },
                    {
                      name: "vomiting",
                      label: "Vomiting"
                    },
                    {
                      name: "gdsLethargicUnconscious",
                      label: "Lethargic/Unconscious"
                    },
                    {
                      name: "convulsion",
                      label: "Convulsion"
                    }
                  ].map((item) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={item.name}>
                      <InputComponents
                        type="checkbox"
                        name={item.name}
                        control={control}
                        label={item.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </div>
            {/* ari */}
            <div>
              <InputComponents
                control={control}
                name="acuteRespiratoryInfection"
                label="Acute Respiratory Infection (ARI)"
                type="radio"
                radioOptions={[
                  {
                    value: CONFIRMATION.YES,
                    label: "Yes"
                  },
                  {
                    value: CONFIRMATION.NO,
                    label: "No"
                  }
                ]}
              />
              {showAcuteRespiratoryInfection && (
                <Grid
                  container
                  spacing={0}
                  sx={{
                    pl: 2,
                    borderLeft: "1px solid #ddd"
                  }}
                >
                  <Grid size={{ xs: 12, sm: 6 }} alignItems="center">
                    <FormTextField
                      style={{ marginTop: "1rem" }}
                      control={control}
                      label="Days"
                      dataTestmation="ariDaysInput"
                      errors={errors}
                      name="ariDays"
                    />
                    <FormTextField
                      control={control}
                      label="Respiratory rate"
                      dataTestmation="respiratoryRate"
                      errors={errors}
                      name="respiratoryRate"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    {[
                      {
                        name: "chestIndrawing",
                        label: "Chest Indrawing"
                      },
                      {
                        name: "striodor",
                        label: "Striodor"
                      },
                      {
                        name: "wheezing",
                        label: "Wheezing"
                      }
                    ].map((item) => (
                      <Stack key={item.name}>
                        <InputComponents
                          type="checkbox"
                          name={item.name}
                          control={control}
                          label={item.label}
                        />
                      </Stack>
                    ))}
                  </Grid>
                </Grid>
              )}
            </div>
            {/* diarrhoea */}
            <div>
              <InputComponents
                control={control}
                name="diarrhoea"
                label="Diarrhoea"
                type="radio"
                radioOptions={[
                  {
                    value: CONFIRMATION.YES,
                    label: "Yes"
                  },
                  {
                    value: CONFIRMATION.NO,
                    label: "No"
                  }
                ]}
              />
              {showDiarrhoea && (
                <Grid
                  container
                  spacing={0}
                  sx={{
                    pl: 2,
                    borderLeft: "1px solid #ddd"
                  }}
                >
                  <Grid size={{ xs: 12, sm: 6 }} alignItems="center">
                    <FormTextField
                      style={{ marginTop: "1rem" }}
                      control={control}
                      label="Days"
                      dataTestmation="diarrhoeaDaysInput"
                      errors={errors}
                      name="diarrhoeaDays"
                    />
                    {[
                      {
                        name: "diarrhoeaUnableToDrink",
                        label: "Unable To Drink"
                      },
                      {
                        name: "diarrhoeaLethargicUnconscious",
                        label: "Lethergic / Unconcious"
                      },
                      {
                        name: "sunkenEyes",
                        label: "Sunken Eyes"
                      }
                    ].map((item) => (
                      <Stack key={item.name}>
                        <InputComponents
                          type="checkbox"
                          name={item.name}
                          control={control}
                          label={item.label}
                        />
                      </Stack>
                    ))}
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    {[
                      {
                        name: "blood",
                        label: "Blood"
                      },
                      {
                        name: "drinksEagerly",
                        label: "Drinks Eagerly"
                      },
                      {
                        name: "irritable",
                        label: "Irritable"
                      },
                      {
                        name: "skinPinchSlowly",
                        label: "Skin pinch slowly"
                      },
                      {
                        name: "skinPinchVerySlowly",
                        label: "Skin pinch very slowly"
                      }
                    ].map((item) => (
                      <Stack key={item.name}>
                        <InputComponents
                          type="checkbox"
                          name={item.name}
                          control={control}
                          label={item.label}
                        />
                      </Stack>
                    ))}
                  </Grid>
                </Grid>
              )}
            </div>
            {/* fever */}
            <div>
              <InputComponents
                control={control}
                name="fever"
                label="Fever"
                type="radio"
                radioOptions={[
                  {
                    value: CONFIRMATION.YES,
                    label: "Yes"
                  },
                  {
                    value: CONFIRMATION.NO,
                    label: "No"
                  }
                ]}
              />
              {showFever && (
                <Grid
                  container
                  spacing={0}
                  sx={{
                    pl: 2,
                    borderLeft: "1px solid #ddd"
                  }}
                >
                  <Grid size={{ xs: 12, sm: 6 }} alignItems="center">
                    <FormTextField
                      style={{ marginTop: "1rem" }}
                      control={control}
                      label="Days"
                      dataTestmation="feverDaysInput"
                      errors={errors}
                      name="feverDays"
                    />
                    {[
                      {
                        name: "stiffNeck",
                        label: "StiffNeck"
                      },
                      {
                        name: "nasalDischarge",
                        label: "Nasal Discharge"
                      },
                      {
                        name: "generalRash",
                        label: "General Rash"
                      },
                      {
                        name: "hazyCornea",
                        label: "Hazy Cornea"
                      }
                    ].map((item) => (
                      <Stack key={item.name}>
                        <InputComponents
                          type="checkbox"
                          name={item.name}
                          control={control}
                          label={item.label}
                        />
                      </Stack>
                    ))}
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <>
                      {[
                        {
                          name: "pusFromEyeOralUlcer",
                          label: "Pus From Eye/Oral Ulcer"
                        },
                        {
                          name: "falciparum",
                          label: "Falciparum"
                        },
                        {
                          name: "nonFalciparum",
                          label: "Non-Falciparum"
                        }
                      ].map((item) => (
                        <Stack key={item.name}>
                          <InputComponents
                            type="checkbox"
                            name={item.name}
                            control={control}
                            label={item.label}
                          />
                        </Stack>
                      ))}
                      {[
                        {
                          name: "malariaRisk",
                          label: "Malarai Risk"
                        },
                        {
                          name: "microscopic",
                          label: "Microscopic"
                        },
                        {
                          name: "rdt",
                          label: "RDT"
                        }
                      ].map((item) => (
                        <InputComponents
                          key={item.name}
                          control={control}
                          name={item.name}
                          label={item.label}
                          type="radio"
                        />
                      ))}
                    </>
                  </Grid>
                </Grid>
              )}
            </div>
            {/* ear infection */}
            <div>
              <InputComponents
                control={control}
                name="earInfection"
                label="Ear Infection"
                type="radio"
                radioOptions={[
                  {
                    value: CONFIRMATION.YES,
                    label: "Yes"
                  },
                  {
                    value: CONFIRMATION.NO,
                    label: "No"
                  }
                ]}
              />
              {showEarInfection && (
                <Grid
                  container
                  spacing={0}
                  sx={{
                    pl: 2,
                    borderLeft: "1px solid #ddd"
                  }}
                >
                  <Grid size={{ xs: 12, sm: 6 }} alignItems="center">
                    <FormTextField
                      style={{ marginTop: "1rem" }}
                      control={control}
                      label="Days"
                      dataTestmation="earInfectionDaysInput"
                      errors={errors}
                      name="earInfectionDays"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    {[
                      {
                        name: "earPain",
                        label: "Ear Pain"
                      },
                      {
                        name: "earDischarge",
                        label: "Ear Discharge"
                      },
                      {
                        name: "tenderSwellingBehindTheEar",
                        label: "Tender Swelling Behind The Ear"
                      }
                    ].map((item) => (
                      <Stack key={item.name}>
                        <InputComponents
                          type="checkbox"
                          name={item.name}
                          control={control}
                          label={item.label}
                        />
                      </Stack>
                    ))}
                  </Grid>
                </Grid>
              )}
            </div>
            {/* nutrition status */}
            <div>
              <Typography fontWeight={500} gutterBottom fontSize="1rem">
                Nutrition Status
              </Typography>
              <Grid
                container
                spacing={0}
                sx={{
                  pl: 2,
                  borderLeft: "1px solid #ddd"
                }}
              >
                {[
                  {
                    name: "severeWasting",
                    label: "Severe Wasting"
                  },
                  {
                    name: "oedemaFeet",
                    label: "Oedema Feet"
                  },
                  {
                    name: "normalWeight",
                    label: "Normal Weight"
                  },
                  {
                    name: "lowWeight",
                    label: "Low Weight"
                  },
                  {
                    name: "veryLowWeight",
                    label: "Very Low Weight"
                  },
                  {
                    name: "somePallor",
                    label: "Some Pallor"
                  },
                  {
                    name: "severePallor",
                    label: "Severe Pallor"
                  }
                ].map((item) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={item.name}>
                    <InputComponents
                      type="checkbox"
                      name={item.name}
                      control={control}
                      label={item.label}
                    />
                  </Grid>
                ))}
                <Grid size={{ xs: 12, sm: 8 }}>
                  <InputComponents
                    control={control}
                    name="muac"
                    label="MUAC"
                    type="radio"
                    radioOptions={[
                      {
                        label: "Green",
                        value: MUAC_OPTIONS.GREEN
                      },
                      {
                        label: "Yellow",
                        value: MUAC_OPTIONS.YELLOW
                      },
                      {
                        label: "Red",
                        value: MUAC_OPTIONS.RED
                      }
                    ]}
                  />
                </Grid>
              </Grid>
            </div>
          </Stack>
        }
      />
      <Collapse
        expanded={expanded === PANELS.CLASSIFICATION_AND_CODE}
        onChange={() => handleExpandChange(PANELS.CLASSIFICATION_AND_CODE)}
        title="Classification And Code"
        contents={
          <Grid container spacing={2} pl={2}>
            <Grid size={12}>
              <FormTextField
                control={control}
                label="Major Classification"
                placeholder="Provide major classification"
                dataTestmation="majorClassification"
                errors={errors}
                name="majorClassification"
                multiline
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormTextField
                fullWidth
                control={control}
                label="Number of classification"
                placeholder="Provide number of classification"
                dataTestmation="majorClassification"
                errors={errors}
                name="numberOfClassification"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} mt={-1}>
              <Diagnosis
                hidetitle
                label="ICD Code"
                diagnosis={icd}
                onChange={(v) => {
                  setValue("icd", v);
                }}
              />
            </Grid>
          </Grid>
        }
      />
      <Collapse
        expanded={expanded === PANELS.TREATMENT_AND_COUNSELLING}
        onChange={() => handleExpandChange(PANELS.TREATMENT_AND_COUNSELLING)}
        title="Treatment And Counselling"
        contents={
          <Grid container spacing={2} pl={2}>
            <Grid size={12}>
              <FormTextField
                fullWidth
                control={control}
                label="Medicines"
                placeholder="Provide medicines"
                dataTestmation="medicinesInput"
                errors={errors}
                name="medicines"
                multiline
              />
            </Grid>
            {/* counselling to mother */}
            <Grid size={12}>
              <Typography fontWeight={600} fontSize="1rem" gutterBottom>
                Counselling to mother
              </Typography>
            </Grid>
            <Box pl={2}>
              <Grid
                container
                spacing={0}
                sx={{
                  pl: 2,
                  borderLeft: "1px solid #ddd"
                }}
              >
                {[
                  {
                    label: "Regular Followup",
                    name: "regularFollowUp"
                  },
                  {
                    label: "Intermediate Visit",
                    name: "intermediateVisit"
                  },
                  {
                    label: "Food",
                    name: "food"
                  },
                  {
                    label: "Fluid",
                    name: "fluid"
                  }
                ].map((item) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={item.name}>
                    <InputComponents
                      control={control}
                      name={item.name}
                      label={item.label}
                      type="checkbox"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
            {/* treatment outcome */}
            <Box pl={2}>
              <InputComponents
                control={control}
                name="treatmentOutcome"
                label="Treatment outcomet"
                type="radio"
                radioOptions={[
                  {
                    label: "Improved",
                    value: OUTCOME.IMPROVED
                  },
                  {
                    label: "Referred",
                    value: OUTCOME.REFERRED
                  },
                  {
                    label: "LAMA/Absconded",
                    value: OUTCOME.LAMA_ABSCONDED
                  },
                  {
                    label: "Death",
                    value: OUTCOME.DEATH
                  }
                ]}
              />
            </Box>
          </Grid>
        }
      />
      <Collapse
        expanded={expanded === PANELS.REFERRED_TO_AND_FOLLOW_UP}
        onChange={() => handleExpandChange(PANELS.REFERRED_TO_AND_FOLLOW_UP)}
        title="Referred To And Follow Up"
        contents={
          <Grid container spacing={2} pl={2}>
            <Grid size={12}>
              <Controller
                name="referredToId"
                control={control}
                render={({ field: { onChange, value }, fieldState }) => (
                  <ReferrerCreateSelect
                    onReferrerChange={(v) => onChange(v?.id || null)}
                    label="Referred To"
                    error={{
                      value: Boolean(fieldState.error),
                      message: fieldState.error?.message || ""
                    }}
                    referrerId={value}
                    textFieldWidth="100%"
                    width="100%"
                    textFieldProps={{
                      InputLabelProps: { shrink: true }
                    }}
                  />
                )}
              />
            </Grid>
            <Divider />
            <Grid size={12} sm={5}>
              <Controller
                name="followUpDate"
                control={control}
                rules={{
                  validate: (value) =>
                    (value ? !!isISODateString(value) : true) || "Please enter valid date"
                }}
                render={({ field }) => (
                  <ToggleInputDate
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    key="followUpDate"
                    showAgeField={false}
                    field={fields[4]}
                    data={{
                      followUpDate: ""
                    }}
                    changeDate={(value) => {
                      field.onChange(value);
                    }}
                    label="Followup"
                    error={!!errors?.dob?.message}
                    helperText={errors?.dob?.message || ""}
                    isFocused={() => ({})}
                    isBlurred={() => ({})}
                  />
                )}
              />
            </Grid>
            <Grid size={7} alignSelf="center">
              <InputComponents
                control={control}
                name="result"
                label="Results"
                type="radio"
                radioOptions={[
                  {
                    label: "Improved",
                    value: RESULT_OPTIONS.IMPROVED
                  },
                  {
                    label: "Same",
                    value: RESULT_OPTIONS.SAME
                  },
                  {
                    label: "Worse",
                    value: RESULT_OPTIONS.WORSE
                  }
                ]}
              />
            </Grid>
          </Grid>
        }
      />
      <Collapse
        expanded={expanded === PANELS.REMARKS}
        onChange={() => handleExpandChange(PANELS.REMARKS)}
        title="Remarks"
        contents={
          <Grid container spacing={2} pl={2}>
            <Grid size={12}>
              <FormTextField
                fullWidth
                control={control}
                label="Remarks"
                placeholder="Provide remarks"
                dataTestmation="majorClassification"
                errors={errors}
                name="remarks"
                multiline
              />
            </Grid>
          </Grid>
        }
      />
    </div>
  );
};

export default CollapsableInputs;
